import Filter from "@/components/ui/filters";
import {
  Badge,
  Button,
  Icon,
  RowCard,
  TablePaginantion,
  useToast,
} from "@elaniintc/goes-design-system";
import SortDropdown from "../../procesos/components/SortDropdown";
import { useEffect, useState } from "react";
import {
  filterTypesEnum,
  sortDirectionEnum,
  type CurrentFiltersType,
  type FilterTypeList,
  type MultiEntitySelections,
  type SortDirectionType,
  type SortOptionsTypes,
} from "@/types/common/filters";
import SearchInput from "@/components/common/search-input";
import { parseAsString, useQueryState } from "nuqs";
import NoData from "@/components/ui/no-data";
import { useDebounceValue } from "usehooks-ts";
import { Skeleton } from "@/components/ui/skeleton";
import { NavLink } from "react-router";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { cn } from "@/utils/theme";
import { formatDateToCommaString, formatToRelativeTime } from "@/utils/dates";
import { getStatusBadgeColor } from "@/utils/templates-ui";
import {
  useGetEmailTemplates,
  useActivateEmailTemplate,
  useDeactivateEmailTemplate,
  type EmailTemplate,
} from "@/queries/templates/email-templates.api";
import EmailTemplateDropdown from "./EmailTemplateDropdown";
import NewEmailTemplateModal from "./NewEmailTemplateModal";
import EditEmailTemplateModal from "./EditEmailTemplateModal";
import DeactivateEmailTemplateModal from "./DeactivateEmailTemplateModal";
import ActivateEmailTemplateModal from "./ActivateEmailTemplateModal";

const EmailTemplatesList = () => {
  const { hasPermission } = useAuthorization();
  const canCreate = hasPermission(Permissions["email-templates"].create);
  const canReadRegistry = hasPermission(Permissions["email-templates"].read);

  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.asc,
  );

  const [page, setPage] = useState(1);
  const [selectedEntities, setSelectedEntities] =
    useState<MultiEntitySelections>({});
  const [query, setQuery] = useQueryState("search", parseAsString);
  const [searchInput, setSearchInput] = useState(query || "");
  const [orderField, setOrderField] = useState<string | undefined>("name");
  const [filters, setFilters] = useState<CurrentFiltersType[] | []>([]);
  const [deferredQuery] = useDebounceValue(searchInput || "", 500);

  // Convert filters from CurrentFiltersType[] to the API format
  const apiFilters = filters.reduce(
    (acc, filter) => {
      if (filter.status) {
        acc.status = filter.status;
      }
      if (filter.name) {
        acc.name = filter.name;
      }
      if (filter.search) {
        acc.search = filter.search;
      }
      return acc;
    },
    {} as { name?: string; status?: string; search?: string },
  );

  const {
    data: emailTemplatesData,
    isLoading,
    isFetching,
    refetch,
  } = useGetEmailTemplates({
    page,
    per_page: 20,
    order_by: orderField,
    sort_direction: orderDirection,
    ...(deferredQuery ? { search: deferredQuery } : {}),
    filters: apiFilters,
  });

  const handleapplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setPage(page);
    setOrderField(field);
    setOrderDirection(direction);
  };

  const { showToast } = useToast();
  const { mutateAsync: activateTemplate, isPending: isActivating } =
    useActivateEmailTemplate();
  const { mutateAsync: deactivateTemplate, isPending: isDeactivating } =
    useDeactivateEmailTemplate();
  const [isOpenNewModal, setIsOpenNewModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenActivateModal, setIsOpenActivateModal] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [templateToDeactivate, setTemplateToDeactivate] = useState<
    string | null
  >(null);

  const handleEdit = (id: string) => {
    const template = emailTemplatesData?.data.find((t) => t.id === id);
    if (template) {
      setSelectedTemplate(template);
      setIsOpenEditModal(true);
    }
  };

  const handleDuplicate = () => {
    showToast({
      title: "Próximamente",
      description: "Funcionalidad de duplicar en desarrollo",
      options: { type: "info", duration: 3000 },
    });
  };

  const [templateToActivate, setTemplateToActivate] = useState<string | null>(
    null,
  );
  const handleActivate = (id: string) => {
    setIsOpenActivateModal(true);
    setTemplateToActivate(id);
  };

  const handleDeactivate = (id: string) => {
    setTemplateToDeactivate(id);
    setIsDeactivateModalOpen(true);
  };

  const confirmDeactivate = async () => {
    if (!templateToDeactivate) return;
    try {
      await deactivateTemplate(templateToDeactivate);
      showToast({
        title: "Plantilla desactivada",
        description: "La plantilla ha sido desactivada exitosamente",
        options: { type: "success", duration: 3000 },
      });
      setIsDeactivateModalOpen(false);
      setTemplateToDeactivate(null);
      refetch();
    } catch {
      showToast({
        title: "Error",
        description: "No se pudo desactivar la plantilla",
        options: { type: "error", duration: 3000 },
      });
    }
  };

  const onActivate = async () => {
    if (!templateToActivate) return;
    try {
      await activateTemplate(templateToActivate);
      showToast({
        title: "Plantilla activada",
        description: "La plantilla ha sido activada exitosamente",
        options: { type: "success", duration: 3000 },
      });
      refetch();
      setIsOpenActivateModal(false);
      setTemplateToActivate(null);
    } catch {
      showToast({
        title: "Error",
        description: "No se pudo activar la plantilla",
        options: { type: "error", duration: 3000 },
      });
    }
  };

  const sortFields: SortOptionsTypes[] = [
    { label: "Nombre", value: "name" },
    { label: "Estado", value: "status" },
    { label: "Fecha de actualización", value: "updated_at" },
    { label: "Fecha de creación", value: "created_at" },
  ];

  const FilterFields: FilterTypeList[] = [
    {
      label: "Fecha de creación",
      type: filterTypesEnum.dates,
      field: "created_at",
    },
    {
      label: "Última Actualización",
      type: filterTypesEnum.dates,
      field: "updated_at",
    },
    {
      label: "Estado",
      type: filterTypesEnum.status,
      field: "status",
      options: [
        { label: "Activa", field: "active" },
        { label: "Inactiva", field: "inactive" },
        { label: "En Construcción", field: "draft" },
      ],
    },
  ];

  useEffect(() => {
    setQuery(deferredQuery || null);
    setPage(1);
  }, [deferredQuery, setQuery, setPage]);

  useEffect(() => {
    if (searchInput === "") {
      refetch();
    }
  }, [searchInput, refetch]);

  const NoDataToShow = () => {
    return (
      <div className="flex flex-col justify-center items-center min-h-[calc(100vh-210px)]">
        <NoData
          title="Aún no existen plantillas de correos"
          description="Crea una plantilla de correo para enviar notificaciones en tus procesos"
        >
          {canCreate && (
            <Button
              variant="default"
              size="sm"
              className="items-center gap-2 rounded-lg cursor-pointer leading-2xl"
              onClick={() => setIsOpenNewModal(true)}
            >
              Nueva plantilla
              <Icon type="far" name="plus" className="text-current" />
            </Button>
          )}
        </NoData>
      </div>
    );
  };

  const isInitialNoData =
    !isLoading &&
    !isFetching &&
    (!emailTemplatesData?.data || emailTemplatesData?.data.length === 0) &&
    !searchInput &&
    !deferredQuery &&
    filters.length === 0;

  return (
    <>
      {isInitialNoData ? (
        <NoDataToShow />
      ) : (
        <div className="flex-1 px-2 pt-8 w-full">
          <div className="flex flex-col gap-8">
            <p className="font-normal text-body leading-3xl">
              Crea y edita las plantillas disponibles en tu organización.
            </p>
            <div className="flex flex-col gap-4">
              <div className="flex justify-between">
                <div className="flex space-x-3">
                  <SortDropdown
                    currentOrderField={orderField}
                    currentOrderDirection={orderDirection}
                    sortOptions={sortFields}
                    onClick={handleapplySort}
                  />
                  <Filter
                    filters={FilterFields}
                    currentFilters={filters}
                    onCurrentFiltersChange={(e: CurrentFiltersType[]) => {
                      setFilters(e);
                      setPage(1);
                    }}
                    selecteds={selectedEntities}
                    onSetSelecteds={setSelectedEntities}
                  />
                </div>
                <div className="flex flex-column gap-2">
                  <SearchInput
                    data-testid="search-input-email-templates"
                    variant="xs"
                    placeholder="Buscar plantillas..."
                    className="h-9 w-[240px] text-sm"
                    value={searchInput || ""}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {canCreate && (
                    <Button
                      variant="default"
                      size="sm"
                      className="items-center gap-2 cursor-pointer py-1.5 px-2"
                      onClick={() => setIsOpenNewModal(true)}
                    >
                      Nueva plantilla
                      <Icon type="far" name="plus" className="text-current" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="w-full flex flex-col gap-2">
                {isLoading || isFetching ? (
                  <div
                    className="w-full flex flex-col mt-2 gap-2"
                    data-testid="page-loader"
                  >
                    {Array.from({ length: 7 }).map((_, idx) => (
                      <Skeleton
                        key={idx}
                        className="w-full gap-4 h-[68px] rounded-xl px-4 py-3"
                      />
                    ))}
                  </div>
                ) : !emailTemplatesData?.data?.length ? (
                  <div className="w-full flex justify-center items-center min-h-[500px]">
                    <h2 className="text-heading-quiet font-semibold text-2xl">
                      Sin resultados
                    </h2>
                  </div>
                ) : (
                  <div className="w-full flex flex-col gap-2 overflow-y-hidden">
                    <div className="flex flex-col gap-2 flex-auto overflow-y-auto">
                      {emailTemplatesData?.data?.map((template, idx) => {
                        return (
                          <NavLink
                            key={template.id}
                            to={`/plantillas/correos/${template.id}`}
                            onClick={(e) => {
                              if (!canReadRegistry) {
                                e.preventDefault();
                              }
                            }}
                            className={cn(!canReadRegistry && "cursor-default")}
                          >
                            <RowCard
                              key={template.id + idx}
                              title={template.name}
                              subtitle={`Última actualización ${formatToRelativeTime(template.updated_at)}  |  Creado el ${formatDateToCommaString(template.created_at)}`}
                              icon={{ name: "envelope" }}
                            >
                              <RowCard.Right>
                                <Badge
                                  size="sm"
                                  className="rounded-lg"
                                  shouldHover={false}
                                  variant={getStatusBadgeColor(template.status)}
                                >
                                  {template.status === "active"
                                    ? "Activa"
                                    : template.status === "draft"
                                      ? "En Construcción"
                                      : "Inactiva"}
                                </Badge>
                                <EmailTemplateDropdown
                                  id={template.id}
                                  status={template.status}
                                  hasContent={!!template.rich_text}
                                  onEdit={handleEdit}
                                  onDuplicate={handleDuplicate}
                                  onDeactivate={handleDeactivate}
                                  onActivate={handleActivate}
                                />
                              </RowCard.Right>
                            </RowCard>
                          </NavLink>
                        );
                      })}
                    </div>
                    {/* Footer */}
                    <div className="p-3">
                      {emailTemplatesData?.meta !== undefined &&
                        emailTemplatesData?.links !== undefined && (
                          <TablePaginantion
                            totalPages={
                              emailTemplatesData?.meta?.last_page || 0
                            }
                            totalItems={emailTemplatesData?.meta?.total || 0}
                            pageSize={emailTemplatesData?.data.length || 0}
                            currentPage={
                              emailTemplatesData?.meta?.current_page || 1
                            }
                            hasNext={
                              (emailTemplatesData?.meta?.current_page || 0) <
                              (emailTemplatesData?.meta?.last_page || 0)
                            }
                            hasPrevious={
                              (emailTemplatesData?.meta?.current_page || 0) > 1
                            }
                            onPageChange={(page) => {
                              setPage(page);
                            }}
                            onNext={() => {
                              if (
                                emailTemplatesData?.meta?.current_page &&
                                emailTemplatesData?.meta?.last_page
                              ) {
                                if (
                                  emailTemplatesData.meta?.current_page <
                                  emailTemplatesData.meta?.last_page
                                ) {
                                  setPage(
                                    emailTemplatesData.meta?.current_page + 1,
                                  );
                                }
                              }
                            }}
                            onPrevious={() => {
                              if (
                                emailTemplatesData?.meta?.current_page &&
                                emailTemplatesData?.meta?.last_page
                              ) {
                                if (emailTemplatesData.meta?.current_page > 1) {
                                  setPage(
                                    emailTemplatesData.meta?.current_page - 1,
                                  );
                                }
                              }
                            }}
                          />
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <NewEmailTemplateModal
        open={isOpenNewModal}
        onClose={() => setIsOpenNewModal(false)}
        onSuccess={() => {
          refetch();
        }}
      />

      {selectedTemplate && (
        <EditEmailTemplateModal
          open={isOpenEditModal}
          templateData={{
            id: selectedTemplate.id,
            name: selectedTemplate.name,
            description: selectedTemplate.description || "",
          }}
          canDelete={selectedTemplate.status === "active"}
          onClose={() => {
            setIsOpenEditModal(false);
            setSelectedTemplate(null);
          }}
          onSuccess={() => {
            refetch();
          }}
          onDeactivate={() => {
            refetch();
          }}
        />
      )}

      <ActivateEmailTemplateModal
        isOpen={isOpenActivateModal}
        isLoading={isActivating}
        onClose={() => setIsOpenActivateModal(false)}
        onAccept={onActivate}
      />

      <DeactivateEmailTemplateModal
        isOpen={isDeactivateModalOpen}
        isLoading={isDeactivating}
        onClose={() => {
          setIsDeactivateModalOpen(false);
          setTemplateToDeactivate(null);
        }}
        onAccept={confirmDeactivate}
      />
    </>
  );
};

export { EmailTemplatesList };
